import React, { useState } from 'react';
import { Utensils, MessageCircle, Dumbbell, CalendarCheck, ShoppingCart, BookOpen, Check, Sparkles, X, Star, ShieldCheck, Lock, Award, Users, ChevronDown, HeartHandshake, AlertTriangle, TrendingDown } from 'lucide-react';

const PricingPage: React.FC = () => {
  const [billing, setBilling] = useState<'monthly' | 'upfront'>('monthly');
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const stats = [
    { icon: <Award className="w-6 h-6" />, value: "12+", label: "Years Experience" },
    { icon: <Users className="w-6 h-6" />, value: "500+", label: "Women Transformed" },
    { icon: <ShieldCheck className="w-6 h-6" />, value: "14 Days", label: "Money-Back Guarantee" },
  ];

  const faqs = [
    { q: "Do I need any gym experience?", a: "No. You don't need to be experienced — you just need to have a dream and the desire to see it through. Everything is built around where you are right now." },
    { q: "What if I want to cancel?", a: "Monthly plans have no long-term contract — cancel anytime. The 3-month upfront option locks in your rate for the full term you've paid for." },
    { q: "How does the WhatsApp support actually work?", a: "You get direct access to me, not a call centre. Message any time and I'll personally reply, adjust your plan, and keep you accountable." },
    { q: "What's the difference between the two plans?", a: "Nutrition & Lifestyle covers your meal plan, support and check-ins. Complete Coaching includes all of that plus a full training programme built around your routine." },
  ];

  const sharedFeatures = [
    { icon: <Utensils className="w-5 h-5" />, title: "Personalised Meal Plans", desc: "Built around the foods you already have access to and enjoy." },
    { icon: <MessageCircle className="w-5 h-5" />, title: "Daily WhatsApp Support", desc: "Direct access to me, whenever you need it." },
    { icon: <ShoppingCart className="w-5 h-5" />, title: "Shopping & Supplement Lists", desc: "Budget-friendly lists to save you money, not spend more." },
    { icon: <BookOpen className="w-5 h-5" />, title: "Ongoing Resources", desc: "The latest knowledge in health and fitness, kept up to date for you." },
  ];

  const monthlyCheckIn = { icon: <CalendarCheck className="w-5 h-5" />, title: "Monthly Progress Check", desc: "One check-in every month to track progress and adjust your plan." };
  const bimonthlyCheckIn = { icon: <CalendarCheck className="w-5 h-5" />, title: "Bi-Monthly Progress Checks", desc: "Two check-ins a month, so we can adjust quickly and keep you on track." };
  const trainingFeature = { icon: <Dumbbell className="w-5 h-5" />, title: "Full Training Programming", desc: "A complete exercise routine built around your actual lifestyle — not a generic template." };
  const wellbeingFeature = { icon: <HeartHandshake className="w-5 h-5" />, title: "Emotional Well-Being & Lifestyle Coaching", desc: "Extra support from an emotional well-being and lifestyle coach, whenever you need it." };

  const plans = [
    {
      name: "Nutrition & Lifestyle",
      tagline: "The foundation — get your eating and habits right.",
      monthly: 99,
      features: [sharedFeatures[0], sharedFeatures[1], monthlyCheckIn, sharedFeatures[2], sharedFeatures[3]],
      notIncluded: [trainingFeature, wellbeingFeature],
      highlight: false,
    },
    {
      name: "Complete Coaching",
      tagline: "Everything in Nutrition & Lifestyle, plus full training.",
      monthly: 150,
      features: [sharedFeatures[0], sharedFeatures[1], bimonthlyCheckIn, sharedFeatures[2], sharedFeatures[3], trainingFeature, wellbeingFeature],
      notIncluded: [],
      upfrontNote: { total: 450, then: 91 },
      highlight: true,
    },
  ];

  const weekly = (m: number) => (m * 12 / 52).toFixed(2);
  const upfront = (m: number) => m * 3;

  return (
    <div className="relative min-h-screen bg-white text-zinc-900 font-sans px-4 py-16 overflow-hidden">

      {/* AMBIENT BACKGROUND GLOW */}
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-red-100 blur-3xl opacity-60" />
      <div className="pointer-events-none absolute top-96 -right-40 w-[500px] h-[500px] rounded-full bg-red-50 blur-3xl opacity-70" />

      <div className="relative max-w-5xl mx-auto">

        {/* LOGO */}
        <div className="flex justify-center mb-10">
          <img
            src="https://firebasestorage.googleapis.com/v0/b/accountability-566c2.firebasestorage.app/o/088529ce-52f4-4ef7-a65f-0923d5901386.png?alt=media"
            alt="Z.A Training Logo"
            className="h-12 md:h-14 object-contain"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        </div>

        {/* HEADER */}
        <div className="text-center mb-8">
          <p className="text-red-700 text-xs font-black uppercase tracking-[0.35em] mb-4">Z.A Training</p>
          <h1 className="text-4xl md:text-6xl font-black uppercase leading-tight mb-4 tracking-tight">
            Choose Your <span className="text-red-700">Plan</span>
          </h1>
          <p className="text-zinc-500 text-base md:text-lg max-w-xl mx-auto font-medium">
            Two ways to work with me. Both built around your life, not a generic template.
          </p>
        </div>

        {/* TRUST BAR */}
        <div className="flex justify-center mb-14">
          <div className="inline-flex items-center gap-4 bg-white border border-zinc-200 shadow-sm rounded-full pl-3 pr-6 py-2.5">
            <div className="flex -space-x-3">
              {['avatar-1', 'avatar-2', 'avatar-3', 'avatar-4', 'avatar-5'].map((a, i) => (
                <img
                  key={i}
                  src={`/avatars/${a}.jpg`}
                  alt="Client"
                  className="w-9 h-9 rounded-full object-cover border-2 border-white"
                />
              ))}
            </div>
            <div className="text-left">
              <div className="flex gap-0.5 text-amber-400 mb-0.5">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-3 w-3 fill-current" />)}
              </div>
              <p className="text-zinc-700 text-xs md:text-sm font-bold">Trusted by 1000+ South Asians</p>
            </div>
          </div>
        </div>

        {/* STATS */}
        <div className="grid grid-cols-3 gap-3 md:gap-6 mb-16 max-w-2xl mx-auto">
          {stats.map((s, i) => (
            <div key={i} className="text-center bg-white border border-zinc-100 rounded-2xl py-5 px-2 shadow-sm">
              <div className="inline-flex text-red-700 mb-2">{s.icon}</div>
              <p className="text-xl md:text-2xl font-black text-zinc-900">{s.value}</p>
              <p className="text-zinc-400 text-[10px] md:text-xs font-bold uppercase tracking-wide mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* MOUNJARO WARNING */}
        <div className="bg-white border-2 border-red-700 rounded-3xl p-8 md:p-10 mb-16 shadow-[0_25px_60px_-15px_rgba(185,28,28,0.25)] max-w-2xl mx-auto">
          <div className="flex justify-center mb-4">
            <div className="inline-flex items-center gap-2 bg-red-700 text-white text-[11px] font-black uppercase tracking-widest px-4 py-1.5 rounded-full">
              <AlertTriangle className="w-3.5 h-3.5" /> For Those Considering GLP-1 Jabs
            </div>
          </div>
          <p className="text-center text-zinc-900 text-lg md:text-xl font-bold leading-snug mb-6">
            Mounjaro costs <span className="text-red-700">£250–£300 a month</span> —
            that's <span className="text-red-700">£1,500–£1,800</span> over just 6 months.
          </p>
          <div className="flex items-start gap-3 bg-red-50 border border-red-100 rounded-2xl p-5">
            <TrendingDown className="w-6 h-6 text-red-700 shrink-0 mt-0.5" />
            <p className="text-zinc-600 text-sm md:text-base font-medium leading-relaxed">
              And even when it works, studies show most people <span className="font-black text-zinc-900">regain the weight</span> once they stop taking it — because it never actually changed the habits behind it. The results don't stick.
            </p>
          </div>
        </div>

        {/* BILLING TOGGLE */}
        <div className="flex justify-center mb-4">
          <div className="inline-flex bg-zinc-100 rounded-full p-1.5 border border-zinc-200">
            <button
              onClick={() => setBilling('monthly')}
              className={`px-6 py-2.5 rounded-full text-sm font-black uppercase tracking-wide transition-all ${
                billing === 'monthly' ? 'bg-red-700 text-white shadow-md' : 'text-zinc-500'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBilling('upfront')}
              className={`px-6 py-2.5 rounded-full text-sm font-black uppercase tracking-wide transition-all ${
                billing === 'upfront' ? 'bg-red-700 text-white shadow-md' : 'text-zinc-500'
              }`}
            >
              Pay Upfront · 3 Months
            </button>
          </div>
        </div>
        <p className="text-center text-zinc-400 text-xs md:text-sm font-semibold mb-14">
          {billing === 'upfront'
            ? 'Pay once, lock in your spot, and commit fully for 3 months.'
            : 'Cancel anytime. No long-term contract required.'}
        </p>

        {/* PRICING CARDS */}
        <div className="grid md:grid-cols-2 gap-8 mb-6 items-start">
          {plans.map((plan, i) => (
            <div
              key={i}
              className={`relative rounded-3xl p-8 md:p-10 bg-white transition-all ${
                plan.highlight
                  ? 'border-2 border-red-700 shadow-[0_25px_60px_-15px_rgba(185,28,28,0.35)] md:-translate-y-3'
                  : 'border border-zinc-200 shadow-sm'
              }`}
            >
              {plan.highlight && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-red-700 text-white text-[11px] font-black uppercase tracking-widest px-4 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg">
                  <Sparkles className="w-3.5 h-3.5" /> Most Popular
                </div>
              )}

              <p className="text-red-700 text-xs font-black uppercase tracking-widest mb-2 text-center">{plan.name}</p>
              <p className="text-zinc-500 text-sm text-center mb-6">{plan.tagline}</p>

              <div className="text-center mb-8">
                {billing === 'monthly' ? (
                  <>
                    <p className="text-5xl md:text-6xl font-black text-zinc-900">
                      £{plan.monthly}<span className="text-lg text-zinc-400 font-bold">/mo</span>
                    </p>
                    <p className="text-zinc-400 text-xs font-bold uppercase tracking-widest mt-2">
                      That's just £{weekly(plan.monthly)}/week
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-5xl md:text-6xl font-black text-zinc-900">
                      £{upfront(plan.monthly)}
                    </p>
                    <p className="text-zinc-400 text-xs font-bold uppercase tracking-widest mt-2">
                      One payment · 3 months · £{plan.monthly}/mo locked in
                    </p>
                    {plan.upfrontNote && (
                      <p className="mt-5 bg-red-50 border border-red-200 rounded-2xl px-4 py-3 text-red-700 text-sm md:text-base font-black leading-snug">
                        Pay £{plan.upfrontNote.total} upfront for the full coaching experience and your monthly repayments are then reduced to £{plan.upfrontNote.then} a month.
                      </p>
                    )}
                  </>
                )}
              </div>

              <div className="space-y-3 mb-8">
                {plan.features.map((f, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-red-700 flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" strokeWidth={3} />
                    </div>
                    <div>
                      <p className="font-black text-sm text-zinc-900">{f.title}</p>
                      <p className="text-zinc-500 text-xs mt-0.5">{f.desc}</p>
                    </div>
                  </div>
                ))}
                {plan.notIncluded.map((f, idx) => (
                  <div key={idx} className="flex items-start gap-3 opacity-40">
                    <div className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-zinc-200 flex items-center justify-center">
                      <X className="w-3 h-3 text-zinc-500" strokeWidth={3} />
                    </div>
                    <div>
                      <p className="font-black text-sm text-zinc-900">{f.title}</p>
                      <p className="text-zinc-500 text-xs mt-0.5">Available on Complete Coaching.</p>
                    </div>
                  </div>
                ))}
              </div>

              <button
                className={`w-full py-4 rounded-xl font-black text-sm uppercase tracking-widest transition-all hover:-translate-y-0.5 ${
                  plan.highlight
                    ? 'bg-red-700 text-white hover:bg-red-800 shadow-lg'
                    : 'bg-zinc-900 text-white hover:bg-zinc-800'
                }`}
              >
                Get Started
              </button>
              <div className="flex items-center justify-center gap-1.5 mt-3">
                <Lock className="w-3 h-3 text-zinc-400" />
                <p className="text-zinc-400 text-[11px] font-bold uppercase tracking-wide">Secure & Personal — Cancel Anytime</p>
              </div>
            </div>
          ))}
        </div>

        {/* TESTIMONIALS */}
        <div className="flex flex-col sm:flex-row justify-center items-center gap-8 mb-16">
          <div className="bg-white p-3 pb-16 rounded-sm shadow-2xl border border-zinc-100 max-w-[280px] -rotate-2 relative">
            <img src="/polaroid-female-1.jpg" alt="Client testimonial" className="w-full rounded-sm" />
            <p className="text-zinc-800 text-sm font-medium italic text-center px-2 mt-4 leading-snug" style={{ fontFamily: 'Georgia, serif' }}>
              "This ended up costing me only £50 a month due to the amount of savings these guys got me, along with all the weight loss. So I actually saved more money working with them. And I lost the weight."
            </p>
          </div>
          <div className="bg-white p-3 pb-16 rounded-sm shadow-2xl border border-zinc-100 max-w-[280px] rotate-2 relative">
            <img src="/polaroid-female-2.jpg" alt="Client testimonial" className="w-full rounded-sm" />
            <p className="text-zinc-800 text-sm font-medium italic text-center px-2 mt-4 leading-snug" style={{ fontFamily: 'Georgia, serif' }}>
              "I'm genuinely kicking myself for not joining sooner. The biggest regret I've got."
            </p>
          </div>
        </div>

        {/* TAGLINE */}
        <div className="text-center mb-16">
          <h2 className="text-2xl md:text-3xl font-black uppercase mb-4 leading-tight">
            PICK YOUR LEVEL. <span className="text-red-700">COMMIT TO THE RESULT.</span>
          </h2>
          <p className="text-zinc-500 text-sm">
            Daily coaching. A plan built around your life. Real, lasting results.
          </p>
        </div>

        {/* FAQ */}
        <div className="max-w-2xl mx-auto mb-16">
          <h2 className="text-2xl md:text-3xl font-black uppercase text-center mb-8 leading-tight">
            QUESTIONS <span className="text-red-700">ANSWERED</span>
          </h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <div key={i} className="bg-white border border-zinc-200 rounded-2xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                >
                  <p className="font-black text-sm md:text-base text-zinc-900">{f.q}</p>
                  <ChevronDown className={`w-4 h-4 text-red-700 shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <p className="text-zinc-500 text-sm px-5 pb-5 -mt-1 leading-relaxed">{f.a}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* GUARANTEE */}
        <div className="bg-red-700 rounded-3xl p-8 md:p-10 text-center shadow-[0_25px_60px_-15px_rgba(185,28,28,0.4)]">
          <p className="text-white/70 text-xs font-black uppercase tracking-[0.3em] mb-3">The Guarantee</p>
          <h2 className="text-2xl md:text-3xl font-black text-white uppercase leading-tight mb-4">
            EXPERIENCE LIFE WITH US<br />FOR 14 DAYS.
          </h2>
          <p className="text-white text-base md:text-lg font-bold leading-snug max-w-md mx-auto">
            If you don't think it's for you, I'll refund you personally — and buy you a burger and fries for wasting your time.
          </p>
        </div>

      </div>
    </div>
  );
};

export default PricingPage;
